import React, { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import DynamicAutoComplete from '../../../../DynamicAutoComplete';
import { TOOLTIPS_TEXT, DEVICE_LOCK_BY_SOMEONE } from '../../../../../../lib/constants';
import InfoPopup from '../../../../../InfoPopup';
import { toast } from 'react-toastify';
import { boxConfig, swapBoxObi } from '../../../../../../actions/EpikBoxes';
import ConfirmationModal from '../../../../../ConfirmationModal';
import { useUnifiedMutate } from '../../../../../../../src/hooks/useUnifiedMutate.js';
import { ResetBoxToDefault } from './ResetBoxToDefault/index.jsx';

const WARNING_TEXT = `This action will migrate all port configurations to the new destination device and return this device to factory settings. Are you sure you want to proceed?`;

const ExpansionModuleAdminTools = memo(
  ({ serialBoxes, removeValueFromArray, onOptionsChange, selectedBox, lockDeviceHandler }) => {
    const token = localStorage.getItem('jwtToken');
    const dispatch = useDispatch();

    const { box } = useSelector(state => state.AppState.boxDetails);
    const { role } = useSelector(state => state.AppState.user);
    const lockStatusBox = useSelector(state => state.UIState.epikBoxes.lockStatusEpikBox);
    const { loading: resetBoxLoading } = useSelector(
      state => state.UIState.epikBoxes.resetBoxToDefault,
    );

    const [warningStatus, setWarningStatus] = useState(false);
    const [boxLockWarning, setBoxLockWarning] = useState(false);
    const [warningText, setWarningText] = useState(WARNING_TEXT);
    const [boxConfigStatus, setBoxConfigStatus] = useState('');

    const { mutate: swapExpansionModule, isLoading: isBoxSwapping } = useUnifiedMutate();

    const isAdmin = ['superAdmin', 'companyGroupAdmin'].includes(role);
    const isSerialBoxesAvailable = serialBoxes.length > 0;

    const handleExecuteClick = async () => {
      if (lockStatusBox?.locked) {
        toast.info(DEVICE_LOCK_BY_SOMEONE);
        return;
      }

      if (!selectedBox) {
        toast.info(`Please select destination serial.`);
        return;
      }

      if (document.getElementById('test-time-remaining')?.hasAttribute('hidden')) {
        setBoxLockWarning(true);
      } else {
        await executeBoxConfig();
      }
    };

    const executeBoxConfig = async () => {
      const response = await dispatch(boxConfig(token, box?.serialNumber, selectedBox, true));
      setBoxConfigStatus(response);
      setWarningText(!response?.blankConfig ? response?.message : WARNING_TEXT);
      if (response?.allowed) setWarningStatus(true);
    };

    const onProceed = async () => {
      if (isBoxSwapping) {
        return;
      }

      const endpoint = 'obis/swap-expansion-module';
      const method = 'PUT';

      await swapExpansionModule({
        isMyEpik: false,
        endpoint,
        method,
        body: {
          currentBox: box?.serialNumber,
          targetBox: selectedBox,
          boxId: box?._id,
          isBoxTab: true,
        },
      });

      setWarningStatus(false);
    };

    return (
      <div className="admin-tools-container">
        {isAdmin && (
          <>
            <h1 className="device heading d-flex">
              Device Swap
              <sup>
                <InfoPopup id="device-swap" text={TOOLTIPS_TEXT.DEVICE_SWAP} />
              </sup>
            </h1>
            <div className="modam-row">
              <div>
                <p className="destination-text">Destination Serial</p>
                <DynamicAutoComplete
                  isDisabled={!isSerialBoxesAvailable}
                  dropdownList={removeValueFromArray(serialBoxes, box?.serialNumber)}
                  onChange={({ suggestion }) => onOptionsChange(suggestion)}
                  placeholder={isSerialBoxesAvailable ? 'Serial Number' : 'No Boxes Found'}
                />
              </div>
              {isSerialBoxesAvailable && (
                <Button
                  onClick={handleExecuteClick}
                  disabled={!selectedBox}
                  color="primary"
                  className="admin-tool-execute-btn"
                >
                  Execute
                </Button>
              )}
              {!boxConfigStatus?.allowed && (
                <p className="font-weight-bold ml-3 mt-2 config-color">
                  {boxConfigStatus?.message}
                </p>
              )}
            </div>

            {/* Reset to Default */}
            <div style={{ marginTop: '20px' }}>
              <ResetBoxToDefault loading={resetBoxLoading} setDcUpdatePopup={setBoxLockWarning} />
            </div>
          </>
        )}

        {warningStatus && (
          <ConfirmationModal
            isOpen={warningStatus}
            onCancel={() => setWarningStatus(false)}
            bodyText={warningText}
            onConfirm={onProceed}
          />
        )}

        {boxLockWarning && (
          <ConfirmationModal
            bodyText="You are attempting to make changes to a device that is not currently locked for editing. To prevent conflicting changes, please lock the device before proceeding."
            headerText="Device Lock Required"
            confirmBtnText="Lock & Edit"
            close="No"
            isOpen={boxLockWarning}
            onCancel={() => setBoxLockWarning(false)}
            onConfirm={() => {
              lockDeviceHandler();
              setBoxLockWarning(false);
            }}
          />
        )}
      </div>
    );
  },
);

export default ExpansionModuleAdminTools;
